"use client";

import { Icon } from "@/components/ui/icon";
import type { DetailedListing } from "@/lib/types";
import { getMapComponents, type MapClient, type MapCoords } from "@repo/maps";
import { publicLocationKind } from "@repo/utils";
import { useMemo, useRef } from "react";

export type ListingMapProps = {
  listing: DetailedListing;
};

const ZOOM_LEVELS = {
  EXACT: 15,
  APPROXIMATE: 13,
  AREA: 10,
};

export function ListingMap({ listing }: ListingMapProps) {
  const mapRef = useRef<MapClient | null>(null);
  const { MapComponent } = getMapComponents<MapCoords>(
    process.env.NEXT_PUBLIC_MAPS_PROVIDER as any,
  );

  const address = listing.business?.address;
  const kind = publicLocationKind({
    visibility: listing.businessAddressVisibility,
    streetAddress1: address?.streetAddress1,
  });
  const place = [address?.city, address?.state].filter(Boolean).join(", ");
  const subdivision = address?.subdivision;

  const center = useMemo(() => {
    if (address?.latitude && address.longitude) {
      return { lat: address.latitude, lng: address.longitude };
    }
    return undefined;
  }, [address?.latitude, address?.longitude]);

  const areaBounds = useMemo(() => {
    if (
      kind !== "AREA" ||
      subdivision?.bboxSwLat == null ||
      subdivision.bboxSwLng == null ||
      subdivision.bboxNeLat == null ||
      subdivision.bboxNeLng == null
    ) {
      return undefined;
    }
    return {
      sw: { lat: subdivision.bboxSwLat, lng: subdivision.bboxSwLng },
      ne: { lat: subdivision.bboxNeLat, lng: subdivision.bboxNeLng },
    };
  }, [
    kind,
    subdivision?.bboxSwLat,
    subdivision?.bboxSwLng,
    subdivision?.bboxNeLat,
    subdivision?.bboxNeLng,
  ]);

  const zoom = kind === "AREA" && !address?.city ? ZOOM_LEVELS.AREA - 3 : ZOOM_LEVELS[kind];

  return (
    <div className="relative h-full w-full">
      <MapComponent
        mapRef={mapRef}
        token={process.env.NEXT_PUBLIC_MAP_TOKEN as string}
        data={kind === "AREA" || !center ? [] : [center]}
        coordsGetter={(coords) => coords}
        renderMarker={({ markerElementRef }) => (
          <div ref={markerElementRef} className="relative">
            {kind === "APPROXIMATE" ? (
              <div
                className="h-24 w-24 rounded-full bg-success/20 ring-1 ring-success/40"
                aria-hidden
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success text-success-foreground">
                <Icon
                  icon={
                    listing.businessType?.iconName
                      ? (`BusinessType.${listing.businessType.iconName}` as any)
                      : "Building2Solid"
                  }
                  className="size-8"
                  cdnBusinessTypeIconVariant="invert"
                />
              </div>
            )}
          </div>
        )}
        className="h-full w-full"
        settings={{
          map: {
            center: areaBounds ? undefined : center,
            zoom: areaBounds ? undefined : zoom,
            maxZoom: kind === "EXACT" ? undefined : zoom,
            bounds: areaBounds,
            scrollSafe: true,
          },
        }}
      />
      {kind === "AREA" && (
        <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-card/95 px-3 py-1.5 text-sm text-foreground shadow-md">
          {place ? `Somewhere in ${place}` : "Somewhere in this area"}
        </div>
      )}
    </div>
  );
}

ListingMap.displayName = "ListingMap";
